import { useEffect, useState } from 'react';
import axios from 'axios';

import type { AppointmentWithThumbnail } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { useDebounce } from 'hooks';
import { featureFlagClient } from 'utils/featureFlagBrowserClient';
import type { VirtualTourWithThumbnail, ESTourItemResponse } from '../types';

const useBuyerAppointments = () => {
  const [isBuyerAppointmentsLoading, setIsBuyerAppointmentsLoading] =
    useState(false);
  const [buyerAppointments, setBuyerAppointments] = useState<
    (AppointmentWithThumbnail | VirtualTourWithThumbnail | ESTourItemResponse)[]
  >([]);
  const [supplementaryTourItems, setSupplementaryTourItems] = useState<
    ESTourItemResponse[]
  >([]);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const fetchAppointments = async () => {
    setIsBuyerAppointmentsLoading(true);
    try {
      const chronologicalShowingsEnabled = featureFlagClient.featureEnabled(
        'chronologicalShowings',
      );

      const {
        data: { appointments: buyerAppt, supplementaryItems },
      } = await axios.get<{
        appointments: AppointmentWithThumbnail[];
        count: number;
        supplementaryItems: ESTourItemResponse[];
      }>('/user/coop/appointments', {
        params: {
          address: debouncedSearchValue,
          page: 0,
          docType: ['virtualTour', 'appointment', 'tourItem'],
          includeSupplementaryTourItems: true,
          ...(chronologicalShowingsEnabled && { sortOrder: 'asc' }),
        },
      });

      setBuyerAppointments(buyerAppt);
      setSupplementaryTourItems(supplementaryItems);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error loading appointments');
    } finally {
      setIsBuyerAppointmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, [debouncedSearchValue]);

  return {
    isBuyerAppointmentsLoading,
    buyerAppointments,
    supplementaryTourItems,
    buyerAppointmentsSearchValue: searchValue,
    setBuyerAppointmentsSearchValue: setSearchValue,
  };
};

export default useBuyerAppointments;
